import { useEffect, useState, type ReactElement } from 'react';
import { useOutletContext, useParams } from 'react-router';
import { lireProjet, modifierProjet } from '../projets.js';
import { EchecApi } from '../api.js';

interface Espace { readonly code: string; readonly nom: string; readonly role: string }

/**
 * LES PARAMETRES D UN PROJET [29-F, dette 57 partielle].
 *
 * LE CODE EST AFFICHE, JAMAIS EDITABLE. Le schema autorise son changement,
 * le produit ne l expose pas : il vit dans les URL, les liens partages et
 * les favoris, et rien ne redirige les anciennes. Le formulaire de creation
 * l annonce deja -- « il ne pourra plus changer ». Cet ecran tient cette
 * promesse au lieu de la contredire.
 *
 * LES DEUX CHAMPS PARTENT ENSEMBLE. Le serveur exige `nom` ET `description` :
 * il n y a pas de patch partiel, donc pas d ambiguite entre « inchange » et
 * « efface ».
 *
 * DETTE 65 -- LE TITRE DE LA COQUILLE NE SE RESYNCHRONISE PAS. Projet.tsx
 * charge le nom UNE FOIS au montage ; un PATCH d ici ne l en informe pas,
 * et l entete garde l ancien nom jusqu au prochain chargement de page.
 * Vu au smoke du 04/09. CONDITION DE LEVEE : trancher OU vit l etat du
 * projet -- remontee par le contexte d Outlet, rechargement de la
 * coquille, ou acceptation explicite du decalage. C est une decision de
 * conception, pas une rustine de fin de lot.
 *
 * DETTE 66 -- LES CHAMPS S AFFICHENT SUR LA LIGNE DE LEUR LIBELLE.
 * L input imbrique dans le label est le motif de TOUS les formulaires
 * [NouveauProjet, NouvelleAutomatisation] : le defaut est dans le CSS,
 * pas ici, et le corriger touchera tous les ecrans a la fois.
 *
 * DETTE 67 -- le titre affiche Parametres sans accent, alors que la
 * rubrique qui y mene est accentuee.
 *
 * NI SUSPENDRE NI ARCHIVER. Les colonnes existent, les gestes n existent pas :
 * leurs consequences sur les automatisations armees et les executions en
 * cours n ont pas ete tranchees. Une rubrique qui les proposerait sans les
 * avoir pensees serait pire que leur absence [dette 57].
 */
export function Parametres(): ReactElement {
  const espace = useOutletContext<Espace>();
  const { projet } = useParams<{ projet: string }>();
  const code = projet ?? '';
  const [nom, setNom] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | undefined>(undefined);
  const [fait, setFait] = useState(false);
  useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const r = await lireProjet(espace.code, code);
        if (vivant) { setNom(r.projet.nom); setDescription(r.projet.description ?? ''); }
      } catch (e) {
        if (vivant) setErreur(e instanceof EchecApi
          ? e.detail.code : 'Chargement impossible.');
      }
    })();
    return () => { vivant = false; };
  }, [espace.code, code]);
  const pret = nom !== undefined && nom.trim() !== '' && !envoi;
  const surEnregistrer = async (): Promise<void> => {
    setEnvoi(true); setErreur(undefined); setFait(false);
    try {
      await modifierProjet(espace.code, code, (nom ?? '').trim(),
        description.trim() === '' ? null : description.trim());
      setFait(true);
    } catch (e) {
      const c = e instanceof EchecApi ? e.detail.code : '';
      setErreur(c === 'droit_insuffisant'
        ? 'Votre role ne permet pas de modifier ce projet.'
        : c === 'corps_invalide'
          ? 'Le nom est obligatoire et limite a 200 caracteres.'
          : e instanceof Error ? e.message : 'Echec inattendu.');
    } finally { setEnvoi(false); }
  };
  if (nom === undefined && erreur !== undefined) {
    return <p className="message message--refus">{erreur}</p>;
  }
  if (nom === undefined) {
    return <p className="discret">Chargement...</p>;
  }
  return (
    <section>
      <h1>Parametres</h1>
      <label>Nom du projet
        <input value={nom} onChange={(e) => { setNom(e.target.value); setFait(false); }} /></label>
      <p className="discret">Le nom lisible, tel qu il s affichera partout.</p>
      <label>Description
        <input value={description}
          onChange={(e) => { setDescription(e.target.value); setFait(false); }} /></label>
      <p className="discret">Facultative. Une phrase pour situer ce projet.</p>
      <p>Code</p>
      <p className="mono">{code}</p>
      <p className="discret">Il apparait dans l adresse du navigateur et ne
        peut pas changer : les liens existants cesseraient de fonctionner.</p>
      {erreur !== undefined && <p className="message message--refus">{erreur}</p>}
      {fait && <p className="discret">Enregistre.</p>}
      <p><button className="bouton" disabled={!pret}
        onClick={() => { void surEnregistrer(); }}>Enregistrer</button></p>
    </section>
  );
}
